import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { CarouselSlideDirective } from '@df/ui-carousel/carousel-slide.directive';
import { CarouselComponent } from '@df/ui-carousel/carousel.component';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { GSAPModule } from '@df/ui/display/gsap/gsap.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { LocalVimeoPlayerComponent } from '../../../../../ui/display/media/vimeo-player.component';
import { MultiColumnBannerAbstract } from '../multi-column-banner/multi-column-banner.abstract';
import {
    PageComponentMultiColumnBannerData,
    PageComponentMultiColumnBannerItemData,
    fields
} from '../multi-column-banner/multi-column-banner.component';

export interface PageComponentMultiColumnBannerV2Data extends PageComponentMultiColumnBannerData {
    items: PageComponentMultiColumnBannerV2ItemData[];
    carousel: boolean;
    carousel_loop: boolean;
    carousel_auto_width: boolean;
    carousel_width: number;
    carousel_nested: boolean;
}

export interface PageComponentMultiColumnBannerV2ItemData extends PageComponentMultiColumnBannerItemData {}

@ICmsComponent<PageComponentMultiColumnBannerV2Component>({
    id: 'multi-column-banner-v2',
    name: 'Multi Column Banner (Variant 2)',
    image: 'assets/images/cms/multi-column-banner-v2.png',
    description: 'Three-column banner',
    tags: ['Images', 'Banner', 'Video'],
    fields: [
        ...fields,
        {
            key: 'carousel',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Carousel',
            description: 'Display items in carousel. Only works on mobile',
            default: false
        },
        {
            key: 'carousel_loop',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Carousel Loop',
            default: false,
            canShow: (data: PageComponentMultiColumnBannerV2Data) => data.carousel
        },
        {
            key: 'carousel_auto_width',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Carousel Auto Width',
            description: 'Custom carousel slide width',
            default: false,
            canShow: (data: PageComponentMultiColumnBannerV2Data) => data.carousel
        },
        <NICmsContentField.INumberField>{
            key: 'carousel_width',
            type: NICmsContentField.EFieldType.number,
            title: 'Carousel Slide Width',
            min: 100,
            max: 375,
            step: 1,
            default: 255,
            canShow: (data: PageComponentMultiColumnBannerV2Data) => data.carousel && data.carousel_auto_width
        },
        <NICmsContentField.IField>{
            key: 'carousel_nested',
            type: NICmsContentField.EFieldType.checkbox,
            title: 'Carousel Nested',
            description: 'Use only on carousels that use same direction as the parent one',
            default: false,
            canShow: (data: PageComponentMultiColumnBannerV2Data) => data.carousel
        }
    ]
})
@Component({
    selector: 'page-component-multi-column-banner-v2',
    templateUrl: './multi-column-banner-v2.html',
    standalone: true,
    imports: [
        CommonModule,
        DfImageDirective,
        CarouselComponent,
        CarouselSlideDirective,
        GridModule,
        GSAPModule,
        HrefComponent,
        ICmsClientModule,
        LocalVimeoPlayerComponent,
        ShellComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponentMultiColumnBannerV2Component extends MultiColumnBannerAbstract implements NICmsContentComponent.IComponent {
    override component!: PageComponentMultiColumnBannerV2Data;
}
